import React, { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { useLocale } from "@/context/LocaleContext";
import { useAuth } from "@/App";
import { persistUserPreferences } from "@/lib/persistUserPreferences";

const THEME_COLORS = {
  light: "#f1f5f9",
  dark: "#05060a",
};

const MATCHED_WIDTH_CLASS = "w-[8.75rem]";

function syncThemeColor(theme) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  meta.setAttribute("content", theme === "dark" ? THEME_COLORS.dark : THEME_COLORS.light);
}

/** Compact Dark | Light toggle; preference stored in localStorage via next-themes. */
export function ThemeSwitch({ className = "", matchedWidth = false }) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t } = useLocale();
  const { user } = useAuth();
  const [mounted, setMounted] = useState(false);
  const active = resolvedTheme === "dark" ? "dark" : "light";

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    syncThemeColor(resolvedTheme || theme);
  }, [mounted, resolvedTheme, theme]);

  const onPick = (next) => {
    setTheme(next);
    if (user && user !== false) {
      persistUserPreferences({ preferred_theme: next }).catch(() => {});
    }
  };

  const btnBase =
    "flex-1 px-3 py-1.5 text-center transition-colors";

  if (!mounted) {
    return (
      <div
        className={`inline-flex rounded-lg border border-[var(--line-2)] overflow-hidden text-xs font-medium opacity-0 ${
          matchedWidth ? MATCHED_WIDTH_CLASS : ""
        } ${className}`}
        aria-hidden
      >
        <span className={btnBase}>&nbsp;</span>
        <span className={btnBase}>&nbsp;</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex rounded-lg border border-[var(--line-2)] overflow-hidden text-xs font-medium ${
        matchedWidth ? MATCHED_WIDTH_CLASS : ""
      } ${className}`}
      role="group"
      aria-label={t("theme.ariaLabel")}
    >
      <button
        type="button"
        data-testid="theme-light"
        onClick={() => onPick("light")}
        className={`${btnBase} ${
          active === "light"
            ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
            : "bg-transparent text-[var(--text-dim)] hover:text-[var(--text)]"
        }`}
      >
        {t("theme.light")}
      </button>
      <button
        type="button"
        data-testid="theme-dark"
        onClick={() => onPick("dark")}
        className={`${btnBase} ${
          active === "dark"
            ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
            : "bg-transparent text-[var(--text-dim)] hover:text-[var(--text)]"
        }`}
      >
        {t("theme.dark")}
      </button>
    </div>
  );
}
